import { formatVehicleIslandCenterSubline } from "../vehicleInfoChrome/vehicleInfoChrome.tools";
import { type PopupContent } from "./vehicleSelection.types";

/**
 * Compares longitude/latitude pairs by value.
 *
 * @param left Prior coordinates.
 * @param right Next coordinates.
 * @returns True when both components are equal.
 */
export function areVehicleSelectionLngLatEqual(
    left: [number, number],
    right: [number, number],
): boolean {
    return left[0] === right[0] && left[1] === right[1];
}

/**
 * Compares realtime popup text fields used by the Dynamic Island bridge.
 *
 * @param previous Prior popup content.
 * @param next Next popup content.
 * @returns True when display-relevant fields are equal.
 */
export function isSamePopupContent( previous: PopupContent, next: PopupContent ): boolean {
    return previous.delay === next.delay &&
      previous.delaySec === next.delaySec &&
      previous.stop_name === next.stop_name &&
      previous.arrival_time === next.arrival_time &&
      previous.trip_headsign === next.trip_headsign &&
      previous.label === next.label &&
      formatVehicleIslandCenterSubline(
          previous.route_type_france,
          previous.trip_headsign,
          previous.trip_headsign_original,
      ) === formatVehicleIslandCenterSubline(
          next.route_type_france,
          next.trip_headsign,
          next.trip_headsign_original,
      );
}

interface VehicleSelectionSnapshot {
    "lngLat":  [number, number];
    "content": PopupContent | undefined;
}

/**
 * Compares React vehicle-selection snapshots from {@link VehicleCustomLayer}.
 *
 * @param previous Prior snapshot.
 * @param next Next snapshot.
 * @returns True when coordinates and popup content are unchanged.
 */
export function areVehicleSelectionSnapshotsEqual(
    previous: VehicleSelectionSnapshot,
    next: VehicleSelectionSnapshot,
): boolean {
    if ( !areVehicleSelectionLngLatEqual( previous.lngLat, next.lngLat ) ) {
        return false;
    }

    if ( previous.content === next.content ) {
        return true;
    }

    if ( previous.content === undefined || next.content === undefined ) {
        return false;
    }

    return isSamePopupContent( previous.content, next.content );
}
